import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, FormGroup, FormLabel, Row } from 'react-bootstrap';
import axiosInstance from '../../../../services/AxiosInstance';
import Api from '../../../../constants/Api';
import { convertDoubleToStringPTBR } from '../../../../utils/ConverterUtils';
import { formatDate } from '../../../../utils/DateUtils';
import { successAlert, warnAlert } from '../../alert/defaultAlert';
import { ApiError } from '../../Common/ApiError';
import AsteriskRequired from '../../Common/AsteriskRequired';
import HintCallout from '../../Common/HintCallout';
import ModalPageEvent from '../../Common/Modal/ModalPageEvent';
import PaymentMethodSelect from '../Common/PaymentMethod/PaymentMethodSelect';
import { offerToOpenCash } from '../CashFlow/Cash/offerToOpenCash';
import { OUTSIDE_PAYMENT_METHODS } from '../Charge/OutsidePaymentAction';

/**
 * Renova o pacote da loja: o cliente paga mais um período no balcão e o valor entra no caixa
 * aberto de quem recebe, como no "Receber por fora".
 *
 * O backend decide quando o novo período começa (StorePackagePeriodService#nextPeriodStart):
 * renovado antes do fim, emenda no período atual; vencido, vale a partir de hoje. A tela só
 * explica a regra, para não existir uma segunda conta de datas aqui.
 *
 * @param {object} subscription assinatura a renovar; nula mantém o modal fechado
 * @param {function} onClose fecha o modal sem renovar
 * @param {function} onRenewed recebe a assinatura atualizada devolvida pelo backend
 */
const StorePackageRenewModal = ({ subscription, onClose, onRenewed }) => {

    const { t } = useTranslation();
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    // O modal sai da frente enquanto o alerta de abrir caixa está aberto, sem perder o pacote.
    const [hidden, setHidden] = useState(false);

    // Cada abertura começa sem forma escolhida: reaproveitar a da última renovação faria o
    // operador confirmar "dinheiro" para quem pagou no cartão sem perceber.
    useEffect(() => {
        setPaymentMethod(null);
        setHidden(false);
    }, [subscription?.id]);

    if (!subscription) {
        return null;
    }

    const confirm = () => {
        if (!paymentMethod?.value) {
            warnAlert(t('validation.required.payment_method'));
            return;
        }
        send(paymentMethod.value);
    };

    // Sem caixa aberto, oferece abrir e repete; qualquer outra recusa mostra o motivo e mantém o
    // modal, porque nada foi registrado.
    const send = (method) => {
        setSubmitting(true);
        axiosInstance.post(`${Api.subscription.subscription}/${subscription.id}/renew-package`, { paymentMethod: method })
            .then(({ data }) => {
                successAlert(t('store_package_renew_success'), () => onRenewed?.(data));
            })
            .catch((error) => {
                const handled = offerToOpenCash({
                    error,
                    branchId: subscription.customer?.branch?.id,
                    question: t('cash_flow.open_cash_before_consumption'),
                    openingNote: t('cash_flow.automatic_opening_for_store_package'),
                    onBeforeAsk: () => setHidden(true),
                    onOpened: () => {
                        setHidden(false);
                        send(method);
                    },
                    onDeclined: () => setHidden(false)
                });
                if (!handled) {
                    ApiError(error);
                }
            })
            .finally(() => setSubmitting(false));
    };

    const validUntil = subscription.storePackageValidUntil ? formatDate(subscription.storePackageValidUntil) : null;

    return (
        <ModalPageEvent
            show={!hidden}
            setShow={(show) => { if (!show) onClose?.(); }}
            centered={true}
            title={t('store_package_renew')}
            btnText={t('confirm')}
            btnOnClick={confirm}
            btnDisabled={submitting}
            child={
                <Row className="g-3">
                    <Col md={12}>
                        <div className="d-flex justify-content-between flex-wrap gap-2">
                            <span>
                                <strong>{subscription.customer?.fullName || '-'}</strong>
                                <span className="d-block text-muted">{subscription.plan?.name || '-'}</span>
                            </span>
                            <strong>
                                {t('currency_symbol')} {subscription.plan?.price != null ? convertDoubleToStringPTBR(subscription.plan.price) : '0,00'}
                            </strong>
                        </div>
                        {validUntil && (
                            <small className="text-muted d-block mt-2">
                                {t(subscription.cancellationDate ? 'store_package_valid_until_past' : 'store_package_valid_until_date',
                                    { date: validUntil })}
                            </small>
                        )}
                    </Col>
                    <Col md={12}>
                        <HintCallout icon="fa-circle-info">
                            {t('store_package_renew_hint')}
                        </HintCallout>
                    </Col>
                    <Col md={12}>
                        <FormGroup>
                            <FormLabel>{t('outside_payment_how')} <AsteriskRequired /></FormLabel>
                            <PaymentMethodSelect
                                value={paymentMethod}
                                onChange={setPaymentMethod}
                                onlyMethods={OUTSIDE_PAYMENT_METHODS}
                            />
                        </FormGroup>
                    </Col>
                </Row>
            }
        />
    );
};

export default StorePackageRenewModal;
